import type { GuideHost, GuideStep, WorldAnchor } from "./guide";
import { GuideLayout, arrowXFor, domHole, placeBubble, placeGlove, placeTip, worldHole, type Box } from "./guideGeometry";
import type { BubbleParts, TipParts } from "./guideWidgets";

/**
 * The overlay's DOM measuring (split from guideOverlay.ts, which draws): where the chrome is, which element or world
 * anchor a step points at, and where its words go. The controller also asks `targetOnScreen` before a `needsTarget`
 * step may start.
 */

/** Chrome the bubble docks against: the lowest bottom of the top set, the highest top of the visible bottom set. */
const TOP_CHROME = ["#hud", ".hub > .topbar"] as const;
const BOTTOM_CHROME = ["#hand", "#controls", ".wave-preview", ".hub > .tabbar"] as const;
/** The "❚❚ Paused" chip of a sim-holding step sits under the top chrome; a top-docked bubble goes below it. */
export const PAUSED_CHIP_SPACE = 40;
/** A bubble whose top is above this (viewport px) sits in the toasts' slot (base.css `.toast` top 112): toasts go under. */
const TOAST_SLOT_BOTTOM = 190;
/** The name plate's offsets in the bubble (guide.css `.g-name` left, and `.name-right` right) and the nib's half-width plus a margin. */
const NAME_LEFT = 96, NAME_RIGHT = 14, NIB_CLEAR = 16;
/** Space between a top-docked bubble (or its chip) and a toast pushed under it. */
const TOAST_GAP = 8;

export const px = (v: number): string => `${Math.round(v)}px`;
export function put(e: HTMLElement, b: Box | null): void {
  if (!b || b.w <= 0 || b.h <= 0) { e.hidden = true; return; }
  e.hidden = false;
  e.style.left = px(b.x); e.style.top = px(b.y); e.style.width = px(b.w); e.style.height = px(b.h);
}
const rectBox = (r: DOMRect, ox: number, oy: number): Box => ({ x: r.left - ox, y: r.top - oy, w: r.width, h: r.height });
function visibleBox(e: Element, ox: number, oy: number): Box | null {
  const r = onScreenRect(e.getBoundingClientRect());
  return r ? { x: r.x - ox, y: r.y - oy, w: r.w, h: r.h } : null;
}

/** The viewport's size (CSS px). */
const viewport = (): { w: number; h: number } => ({ w: window.innerWidth || document.documentElement.clientWidth, h: window.innerHeight || document.documentElement.clientHeight });

/** The part of a rect inside the viewport, or null when none of it is (a target scrolled or slid away). */
export function onScreenRect(r: DOMRect): { x: number; y: number; w: number; h: number } | null {
  const v = viewport();
  const x0 = Math.max(0, r.left), y0 = Math.max(0, r.top), x1 = Math.min(v.w, r.right), y1 = Math.min(v.h, r.bottom);
  return x1 - x0 >= 1 && y1 - y0 >= 1 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
}

/**
 * Drawn: a size, some of it on screen, and not hidden by visibility or opacity on the way up (a parked sheet keeps its
 * box under `visibility: hidden`: M45 found the glove and the nib pointing into one).
 */
export function isShown(e: Element): boolean {
  const r = e.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0 || onScreenRect(r) === null) return false;
  const cv = (e as Element & { checkVisibility?: (o: object) => boolean }).checkVisibility;
  if (typeof cv === "function") return cv.call(e, { visibilityProperty: true, opacityProperty: true });
  for (let x: Element | null = e; x; x = x.parentElement) {
    const c = getComputedStyle(x);
    if (c.visibility === "hidden" || c.display === "none" || c.opacity === "0") return false;
  }
  return true;
}

/**
 * The first shown match (DESIGN §5: "resolves to the first visible match, every layout"); of several, the first one a
 * tap at its middle would reach, so a selector list ("PLAY, CONTINUE") or a duplicate under a sheet picks the one the
 * player can press.
 */
export function firstVisible(sel: string): HTMLElement | null {
  let list: NodeListOf<HTMLElement>;
  try { list = document.querySelectorAll<HTMLElement>(sel); } catch { return null; }
  let first: HTMLElement | null = null;
  for (const e of list) {
    if (!isShown(e)) continue;
    if (list.length === 1) return e;
    first ??= e;
    if (hittable(e)) return e;
  }
  return first;
}

// ------------------------------------------------------------------------------------------------ the surface check (M45)

/**
 * The top-level layer an element belongs to: its ancestor that is a child of the UI root (`#ui`: the hub, a sheet, a
 * dialog, the HUD plate, the end screen…) or of a page-level container (`#app`, holding the 3D view and `#ui`).
 */
export function layerOf(e: Element | null): Element | null {
  for (let x = e; x; x = x.parentElement) {
    const p = x.parentElement;
    if (!p || p === document.body || p.id === "ui") return x;
  }
  return null;
}

/** Everything at (x, y) that takes a tap, topmost first, the guide's own pieces aside (they sit over everything). */
function stackAt(x: number, y: number): Element[] {
  if (typeof document.elementsFromPoint !== "function") return [];
  return document.elementsFromPoint(x, y).filter((e) => !e.closest("#guide"));
}
/** The topmost element at (x, y) that takes a tap, the guide's own pieces aside. */
export function topAt(x: number, y: number): Element | null { return stackAt(x, y)[0] ?? null; }

/** A tap at the middle of the element's on-screen part reaches it (or something inside it, or it is a passive wrapper). */
export function hittable(e: Element): boolean {
  const r = onScreenRect(e.getBoundingClientRect());
  if (!r) return false;
  const hit = topAt(r.x + r.w / 2, r.y + r.h / 2);
  return hit === null || e.contains(hit) || hit.contains(e);
}

/**
 * Is the guide's surface clear for this step? The one check behind "a tip never shows over another screen" (M45, Kevin
 * 2026-10-16: "Brams tips can appear on top of other screens"), with no per-screen cases:
 * - its DOM target is drawn and nothing of another layer covers it: a tap at its middle reaches it, or reaches its own
 *   layer (a pill strip overlapping the hub's own button is still the hub);
 * - its world target, when on screen, is not under anything but the 3D view and the screen's own layer;
 * - and the screen it belongs to is the topmost interactive layer: what a tap at the middle of the screen reaches is
 *   the screen's own surface (GuideHost.screen), the 3D view, or the target's own layer (a sheet the step points into).
 * A target not drawn at all does not make the step covered (it shows without a pointer, or waits when `needsTarget`).
 */
export function surfaceClear(step: GuideStep, host: GuideHost): boolean {
  if (typeof document === "undefined" || typeof document.elementsFromPoint !== "function") return true;
  let screenEl: Element | null = null;
  try { screenEl = host.screen?.() ?? host.root; } catch { screenEl = host.root; }
  const base = layerOf(screenEl);
  const world = layerOf(document.getElementById("ui"));
  const ok = (hit: Element | null, also: Element | null): boolean => {
    if (hit === null) return true;
    const l = layerOf(hit);
    return l === base || (also !== null && l === also) || (hit.tagName === "CANVAS" && l === world);
  };
  const t = step.target;
  let targetLayer: Element | null = null;
  /** The layers painted under the target (a sheet's own scrim, the hub under the sheet): they do not cover it. */
  let under = new Set<Element | null>();
  if (t && "dom" in t) {
    const el = firstVisible(t.dom);
    const r = el ? onScreenRect(el.getBoundingClientRect()) : null;
    if (el && r) {
      targetLayer = layerOf(el);
      const stack = stackAt(r.x + r.w / 2, r.y + r.h / 2);
      const hit = stack[0] ?? null;
      if (hit !== null && !el.contains(hit) && !hit.contains(el) && layerOf(hit) !== targetLayer) return false;
      const at = stack.findIndex((e) => layerOf(e) === targetLayer);
      under = new Set(stack.slice(at + 1).map(layerOf));
    }
  } else if (t && "world" in t) {
    const a = locate(host, t.world);
    if (a) {
      const o = host.root.getBoundingClientRect();
      if (!ok(topAt(a.x + o.left, a.y + o.top), null)) return false;
    }
  }
  const v = viewport();
  const mid = topAt(v.w / 2, v.h / 2);
  return ok(mid, targetLayer) || (targetLayer !== null && under.has(layerOf(mid)));
}

export function bands(oy: number, vh: number): { top: number; bottom: number } {
  let top = 0, bottom = vh;
  for (const s of TOP_CHROME) { const e = firstVisible(s); if (e) top = Math.max(top, e.getBoundingClientRect().bottom - oy); }
  for (const s of BOTTOM_CHROME) {
    for (const e of document.querySelectorAll<HTMLElement>(s)) { const r = e.getBoundingClientRect(); if (r.width > 0 && r.height > 0) bottom = Math.min(bottom, r.top - oy); }
  }
  return { top, bottom: Math.max(top, bottom) };
}

export function locate(host: GuideHost, a: WorldAnchor): ReturnType<NonNullable<GuideHost["world"]>["locate"]> {
  try { return host.world?.locate(a) ?? null; } catch { return null; }
}

/** Whether a step's target is on screen now (a step with no target always is). */
export function targetOnScreen(step: GuideStep, host: GuideHost): boolean {
  const t = step.target;
  if (!t) return true;
  return "dom" in t ? firstVisible(t.dom) !== null : locate(host, t.world) !== null;
}

export interface ResolvedTarget {
  /** The spotlight. */
  readonly holeBox: Box | null;
  /** What the glove points at. */
  readonly pointBox: Box | null;
  readonly world: boolean;
  readonly targetEl: HTMLElement | null;
}

/** The step's spotlight and pointer in overlay px (origin ox, oy; size vw × vh). */
export function resolveTarget(step: GuideStep, host: GuideHost, ox: number, oy: number, vw: number, vh: number): ResolvedTarget {
  const target = step.target;
  if (target && "dom" in target) {
    const targetEl = firstVisible(target.dom);
    // no target on screen, no spotlight either (a row with no tower card in it is not "the tower card")
    const holeEl = targetEl && target.hole ? firstVisible(target.hole) ?? targetEl : targetEl;
    return {
      targetEl, world: false,
      // the part on screen: a nib or glove aims at what the player can see (a row half scrolled away points at its half)
      pointBox: targetEl ? visibleBox(targetEl, ox, oy) : null,
      holeBox: holeEl ? domHole(rectBox(holeEl.getBoundingClientRect(), ox, oy), target.pad ?? GuideLayout.DomPad, vw, vh) : null,
    };
  }
  if (target && "world" in target) {
    const a = locate(host, target.world);
    return { targetEl: null, world: true, holeBox: a ? worldHole(a, target.radiusCells ?? 1) : null, pointBox: a ? { x: a.x - 1, y: a.y - 1, w: 2, h: 2 } : null };
  }
  return { targetEl: null, world: false, holeBox: null, pointBox: null };
}

export interface WordsIn {
  readonly step: GuideStep;
  readonly bubble: BubbleParts | null;
  readonly tip: TipParts | null;
  readonly vw: number; readonly vh: number; readonly oy: number;
  readonly bubbleH: number;
  readonly target: ResolvedTarget;
  readonly topBand: number; readonly bottomBand: number;
}

/**
 * Places the bubble or the tip beside the spotlight. Returns the viewport y below which a toast may show when the
 * words sit in the toasts' slot at the top, else null.
 */
export function placeWords(w: WordsIn): number | null {
  const { holeBox, pointBox, world } = w.target;
  if (w.bubble) {
    const p = placeBubble({ vw: w.vw, vh: w.vh, bubbleH: w.bubbleH, hole: holeBox, world, topBand: w.topBand, bottomBand: w.bottomBand, placement: w.step.placement ?? "auto", chip: w.bubble.chip !== null });
    const wrap = w.bubble.wrap;
    wrap.style.top = px(p.top);
    wrap.classList.toggle("chip-below", p.chip === "below");
    w.bubble.arrow.className = `g-arrow${p.arrow ? ` ${p.arrow}` : ""}`;
    w.bubble.arrow.hidden = p.arrow === null;
    // the nib aims at what the glove points at (one card of the spotlit row), not the middle of the spotlight
    const arrowX = pointBox && !world ? arrowXFor(pointBox.x + pointBox.w / 2, w.vw) : p.arrowX;
    w.bubble.bubble.style.setProperty("--ax", px(arrowX));
    // M45: an up-nib under the "CAPTAIN BRAM" plate was drawn behind it (a target near the left third had no visible
    // pointer at all): the plate moves to the bubble's other end when the nib would sit under it.
    const plate = w.bubble.bubble.querySelector<HTMLElement>(".g-name");
    if (plate) {
      const pw = plate.offsetWidth, bw = w.bubble.bubble.offsetWidth;
      const under = (left: number) => arrowX > left - NIB_CLEAR && arrowX < left + pw + NIB_CLEAR;
      wrap.classList.toggle("name-right", p.arrow === "up" && under(NAME_LEFT) && !under(bw - NAME_RIGHT - pw));
    }
    return p.top + w.oy < TOAST_SLOT_BOTTOM ? p.top + w.bubbleH + (p.chip === "below" && w.bubble.chip ? GuideLayout.ChipSpace : 0) + w.oy + TOAST_GAP : null;
  }
  if (w.tip) {
    const prev = w.tip.tip.dataset.top;
    const p = placeTip({ vw: w.vw, vh: w.vh, tipH: w.bubbleH, target: holeBox ?? pointBox, topBand: w.topBand, bottomBand: w.bottomBand, prevTop: prev ? Number(prev) : null });
    w.tip.tip.dataset.top = String(p.top);
    w.tip.tip.style.top = px(p.top);
    w.tip.arrow.className = `g-arrow${p.arrow ? ` ${p.arrow}` : ""}`;
    w.tip.arrow.hidden = p.arrow === null;
    w.tip.tip.style.setProperty("--ax", px(p.arrowX));
    return p.top + w.oy < TOAST_SLOT_BOTTOM ? p.top + w.bubbleH + w.oy + TOAST_GAP : null;
  }
  return null;
}

/** The glove on what the step points at, or hidden (`pointBox` null). */
export function drawGlove(glove: { readonly hand: HTMLElement; readonly ripple: HTMLElement }, pointBox: Box | null, world: boolean, vw: number, vh: number): void {
  if (!pointBox) { glove.hand.hidden = true; glove.ripple.hidden = true; return; }
  const g = placeGlove(pointBox, world, vw, vh);
  glove.hand.hidden = false; glove.ripple.hidden = false;
  glove.hand.style.left = px(g.x); glove.hand.style.top = px(g.y);
  glove.hand.classList.toggle("fx", g.flipX); glove.hand.classList.toggle("fy", g.flipY);
  glove.ripple.style.left = px(g.tipX); glove.ripple.style.top = px(g.tipY);
}
